import { useState, useCallback } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

interface InitialsEntryProps {
  score: number;
  onSubmit: (initials: string) => void;
  onSkip?: () => void;
}

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const InitialsEntry = ({ score, onSubmit, onSkip }: InitialsEntryProps) => {
  const [slots, setSlots] = useState([0, 0, 0]); // indices into LETTERS
  const [activeSlot, setActiveSlot] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const cycleUp = useCallback((index: number) => {
    setSlots(prev => {
      const next = [...prev];
      next[index] = (next[index] + 1) % 26;
      return next;
    });
  }, []);

  const cycleDown = useCallback((index: number) => {
    setSlots(prev => {
      const next = [...prev];
      next[index] = (next[index] - 1 + 26) % 26;
      return next;
    });
  }, []);

  const handleSubmit = () => {
    if (submitting) return;
    setSubmitting(true);
    const initials = slots.map(i => LETTERS[i]).join('');
    onSubmit(initials);
  };

  return (
    <div className="flex flex-col items-center space-y-8">
      <div className="space-y-1">
        <h2 className="text-2xl md:text-3xl font-display font-bold text-primary text-glow-cyan uppercase tracking-wider">
          New High Score
        </h2>
        <p className="text-lg md:text-xl font-display font-bold text-muted-foreground uppercase tracking-wider">
          Enter Initials
        </p>
      </div>
      <p className="font-mono-game text-muted-foreground text-sm">
        SCORE: <span className="text-primary font-bold">{score}</span>
      </p>

      <div className="flex gap-4 md:gap-6">
        {slots.map((letterIndex, slotIdx) => (
          <div
            key={slotIdx}
            className={`flex flex-col items-center gap-2 cursor-pointer ${
              activeSlot === slotIdx ? 'scale-110' : 'opacity-70'
            } transition-all`}
            onClick={() => setActiveSlot(slotIdx)}
          >
            <button
              onClick={(e) => { e.stopPropagation(); cycleUp(slotIdx); }}
              className="text-primary hover:text-primary/80 transition-colors p-1"
            >
              <ChevronUp className="w-8 h-8" />
            </button>
            <div
              className={`w-16 h-20 md:w-20 md:h-24 flex items-center justify-center border-2 rounded-lg font-mono-game text-4xl md:text-5xl font-bold transition-all ${
                activeSlot === slotIdx
                  ? 'border-primary text-primary shadow-[0_0_20px_hsl(var(--primary)/0.5)] bg-primary/10'
                  : 'border-border text-foreground bg-card'
              }`}
            >
              {LETTERS[letterIndex]}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); cycleDown(slotIdx); }}
              className="text-primary hover:text-primary/80 transition-colors p-1"
            >
              <ChevronDown className="w-8 h-8" />
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={submitting}
        className="mt-4 px-10 py-4 rounded-lg bg-primary text-primary-foreground font-display text-xl font-bold uppercase tracking-wider hover:shadow-[0_0_30px_hsl(var(--primary)/0.5)] hover:scale-[1.02] transition-all disabled:opacity-50 disabled:pointer-events-none"
      >
        {submitting ? 'Submitting…' : 'Submit Score'}
      </button>
      {onSkip && (
        <button
          onClick={onSkip}
          className="mt-2 text-sm font-mono-game text-muted-foreground hover:text-foreground transition-colors uppercase tracking-wider"
        >
          Skip
        </button>
      )}
    </div>
  );
};

export default InitialsEntry;
